import * as Yup from 'yup';
import moment from 'moment';

type MomentDateInputFormats = moment.MomentFormatSpecification;
type DateSchemaCtor = new () => Yup.DateSchema<Date | null | undefined>;
const DateSchema = Yup.date().constructor as DateSchemaCtor;

type MomentDateSchema = Yup.DateSchema<Date | null | undefined> & {
  format: (nextFormats: MomentDateInputFormats) => MomentDateSchema;
};

const createMomentDateTransform =
  (formats: MomentDateInputFormats = []) =>
  (value, originalValue) => {
    if (originalValue === null) {
      return null;
    }

    if (typeof originalValue === 'undefined' || originalValue === '') {
      return undefined;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    if (moment.isMoment(originalValue)) {
      return originalValue.isValid() ? originalValue.toDate() : new Date('');
    }

    const parsedValue = moment(originalValue, formats, true);

    return parsedValue.isValid() ? parsedValue.toDate() : new Date('');
  };

const withFormat = (schema: Yup.DateSchema<Date | null | undefined>): MomentDateSchema => {
  const schemaWithFormat = schema as MomentDateSchema;

  schemaWithFormat.format = (nextFormats: MomentDateInputFormats) => {
    if (!nextFormats) {
      throw new Error('must enter a valid format');
    }

    return withFormat(schema.clone().transform(createMomentDateTransform(nextFormats)));
  };

  return schemaWithFormat;
};

const createMomentDateSchema = (formats: MomentDateInputFormats = []) => {
  const schema = Yup.date().transform(createMomentDateTransform(formats)) as MomentDateSchema;

  return withFormat(schema);
};

class MomentDateSchemaType extends DateSchema {
  constructor() {
    super();

    return createMomentDateSchema() as MomentDateSchemaType;
  }
}

export default MomentDateSchemaType;
